<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { VirtualElement } from '@floating-ui/vue';
import { useFloatingPanel } from '../Popover/useFloatingPanel';

/** A canvas location in viewport pixels, with an optional DOM context. */
export interface NxTooltipPointAnchor {
  x: number;
  y: number;
  contextElement?: HTMLElement;
}

export interface NxTooltipProps {
  /** The bubble's text; the `#content` slot replaces it. */
  text?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  delayMs?: number;
  /**
   * Position the bubble against this element or viewport point without a wrapper.
   * For a surface with many small triggers — calendar days, table cells — where
   * one wrapper per trigger would cost a DOM node each and break the layout or
   * the ARIA structure it sits in. Element anchors handle hover and keyboard
   * focus automatically unless `open` is controlled. Viewport points need `open`.
   */
  anchor?: HTMLElement | NxTooltipPointAnchor | null;
  /** Controlled visibility. Leave undefined for the hover/focus behaviour. */
  open?: boolean;
  /**
   * Opt-in hoverable bubble (WCAG 1.4.13 pointer persistence). Default chrome
   * labels stay click-through and hide immediately on pointer leave.
   * Escape dismissal applies to every uncontrolled tooltip, independent of
   * this flag.
   */
  interactive?: boolean;
  /**
   * Keep the line breaks in `text` and set it flush left — a milestone's dates,
   * one per line. Off by default: a one-line label stays centred.
   */
  multiline?: boolean;
}

const props = withDefaults(defineProps<NxTooltipProps>(), {
  text: '',
  side: 'top',
  delayMs: 0,
  anchor: undefined,
  open: undefined,
  interactive: false,
  multiline: false,
});

defineSlots<{
  /** The trigger the tooltip wraps (omit it in anchored mode). */
  default?: () => unknown;
  /** Rich bubble content — a short list, bold words; replaces `text`. */
  content?: () => unknown;
}>();

const pointerOpen = ref(false);
const focusOpen = ref(false);
const wrapper = ref<HTMLElement | null>(null);

/** The anchor wins; without one the wrapped trigger is the reference. */
const reference = computed<HTMLElement | VirtualElement | null>(() => {
  const anchor = props.anchor;
  if (!anchor || 'getBoundingClientRect' in anchor) return anchor ?? wrapper.value;
  const { x, y, contextElement } = anchor;
  return {
    getBoundingClientRect: () => ({
      x,
      y,
      left: x,
      top: y,
      right: x,
      bottom: y,
      width: 0,
      height: 0,
    }),
    contextElement,
  };
});
const contextElement = computed(() => {
  const anchor = props.anchor;
  return anchor && !('getBoundingClientRect' in anchor)
    ? anchor.contextElement
    : (anchor ?? wrapper.value);
});

/**
 * Where the bubble is teleported to.
 *
 * `<body>` by default — see the template comment for why it leaves the tree at
 * all. But a modal `<dialog>` and an open `[popover]` are promoted to the TOP
 * LAYER, which paints above the whole ordinary document no matter what
 * `z-index` says. A bubble parked in `<body>` therefore opens BEHIND the very
 * modal it belongs to, and no stacking value can lift it out.
 *
 * So when the trigger sits inside one, the bubble goes in there with it: same
 * top layer, correct order, and a dialog is not the narrow clipped rail the
 * `<body>` default exists to escape.
 *
 * The bubble is itself a manual popover in the top layer (`showPopover()` once
 * it is rendered), like `NxPopover`'s panel. In the page it was boxed in by
 * its DOM ancestors: a dialog opened from a grid cell (I-2799) sits in rows and
 * cells that carry a `transform`, inside a scrolling table, and the
 * positioning kept the bubble within that table — flipped and slid away from a
 * trigger the top layer shows elsewhere. In the top layer only the window
 * bounds it.
 */
const teleportTarget = computed<HTMLElement | 'body'>(
  // `closest` is typed `Element | null` without the parameter, and `Element` is
  // not assignable to the declared `HTMLElement` — every app that compiles this
  // package from source then fails to build, while `frontend/erp` stays green
  // because it resolves the package through its built types.
  () => contextElement.value?.closest<HTMLElement>('dialog, [popover]') ?? 'body'
);

/** Controlled when the consumer passes `open`, hover-driven otherwise. */
const isControlled = computed(() => props.open !== undefined);
const open = computed(() => {
  if (props.open !== undefined) return props.open;
  return pointerOpen.value || focusOpen.value;
});
const floating = ref<HTMLElement | null>(null);
const arrowEl = ref<HTMLElement | null>(null);

// Into the top layer as soon as the bubble exists (it renders only while open).
watch(floating, (el) => {
  if (!el || !('showPopover' in el)) return;
  try {
    el.showPopover();
  } catch {
    /* already shown, or the element left the document */
  }
});
let pointerShowTimer: ReturnType<typeof setTimeout> | null = null;
let focusShowTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
const pendingShow = ref(false);

/** Long enough to cross the 8px offset onto the bubble; cancelled on enter. */
const POINTER_CLOSE_DELAY_MS = 120;

const { floatingStyles, placement, middlewareData, update } = useFloatingPanel(
  reference,
  floating,
  {
    open,
    placement: () => props.side,
    offset: 8,
    arrowEl,
  }
);

// Recompute once the teleported bubble is actually in the DOM / open — and
// again when the anchor moves to another element under a bubble already up,
// which is how a hovered grid hands the tooltip from cell to cell.
watch([open, reference], ([isOpen]) => {
  if (isOpen) {
    requestAnimationFrame(() => update());
  }
});

// Poke the beak out of the panel on the side facing the trigger; it follows the
// resolved placement, so it stays correct after flip/shift.
const arrowStyle = computed(() => {
  const data = middlewareData.value.arrow;
  if (!data) return {};
  const base = placement.value.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';
  const staticSide = ({ top: 'bottom', bottom: 'top', left: 'right', right: 'left' } as const)[
    base
  ];
  return {
    left: data.x != null ? `${data.x}px` : '',
    top: data.y != null ? `${data.y}px` : '',
    [staticSide]: '-0.25em',
  } as Record<string, string>;
});

function syncPendingShow() {
  pendingShow.value = pointerShowTimer !== null || focusShowTimer !== null;
}

function clearPointerShowTimer() {
  if (pointerShowTimer) {
    clearTimeout(pointerShowTimer);
    pointerShowTimer = null;
  }
  syncPendingShow();
}

function clearFocusShowTimer() {
  if (focusShowTimer) {
    clearTimeout(focusShowTimer);
    focusShowTimer = null;
  }
  syncPendingShow();
}

function clearHideTimer() {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function armPointerOpen() {
  clearHideTimer();
  clearPointerShowTimer();
  pointerShowTimer = setTimeout(() => {
    pointerOpen.value = true;
    pointerShowTimer = null;
    syncPendingShow();
  }, props.delayMs);
  syncPendingShow();
}

/**
 * Whether focus came from the keyboard. A mouse click focuses the button too —
 * opening then left the tooltip standing until focus moved on. The browser's
 * `:focus-visible` heuristic answers it for a really focused element; a synthetic
 * `focusin` (tests, or a target that is not the active element) keeps opening.
 */
function isKeyboardFocus(event: FocusEvent): boolean {
  const target = event.target;
  if (!(target instanceof Element) || target !== document.activeElement) return true;
  try {
    return target.matches(':focus-visible');
  } catch {
    return true;
  }
}

function armFocusOpen(event: FocusEvent) {
  if (!isKeyboardFocus(event)) return;
  clearHideTimer();
  clearFocusShowTimer();
  focusShowTimer = setTimeout(() => {
    focusOpen.value = true;
    focusShowTimer = null;
    syncPendingShow();
  }, props.delayMs);
  syncPendingShow();
}

function hidePointer() {
  clearPointerShowTimer();
  clearHideTimer();
  pointerOpen.value = false;
}

function scheduleHidePointer() {
  if (!props.interactive) {
    hidePointer();
    return;
  }
  clearPointerShowTimer();
  clearHideTimer();
  hideTimer = setTimeout(() => {
    pointerOpen.value = false;
    hideTimer = null;
  }, POINTER_CLOSE_DELAY_MS);
}

function hideFocus(event: FocusEvent) {
  const next = event.relatedTarget;
  if (next instanceof Node) {
    if (contextElement.value?.contains(next)) return;
    if (floating.value?.contains(next)) return;
  }
  clearFocusShowTimer();
  focusOpen.value = false;
}

function onBubbleEnter() {
  if (!props.interactive) return;
  armPointerOpen();
}

function onBubbleLeave() {
  if (!props.interactive) return;
  scheduleHidePointer();
}

function onEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  if (isControlled.value) return;
  if (!open.value && pointerShowTimer === null && focusShowTimer === null) return;

  clearPointerShowTimer();
  clearFocusShowTimer();
  clearHideTimer();
  pointerOpen.value = false;
  focusOpen.value = false;
}

// Element anchors use the same interaction as wrapped triggers without adding
// a node to layouts such as tile grids. Controlled and virtual anchors stay
// entirely under their owner's control.
watch(
  [() => props.anchor, isControlled],
  ([anchor, controlled], _, onCleanup) => {
    if (controlled || typeof HTMLElement === 'undefined' || !(anchor instanceof HTMLElement))
      return;
    anchor.addEventListener('mouseenter', armPointerOpen);
    anchor.addEventListener('mouseleave', scheduleHidePointer);
    anchor.addEventListener('focusin', armFocusOpen);
    anchor.addEventListener('focusout', hideFocus);
    onCleanup(() => {
      anchor.removeEventListener('mouseenter', armPointerOpen);
      anchor.removeEventListener('mouseleave', scheduleHidePointer);
      anchor.removeEventListener('focusin', armFocusOpen);
      anchor.removeEventListener('focusout', hideFocus);
      clearPointerShowTimer();
      clearFocusShowTimer();
      clearHideTimer();
      pointerOpen.value = false;
      focusOpen.value = false;
    });
  },
  { immediate: true, flush: 'post' }
);

// Uncontrolled only: the owner owns Escape in controlled mode. Window capture
// runs before every document-capture handler regardless of mount order, so a
// v-if-gated tooltip still dismisses. The event is not consumed — a tooltip
// on a popover trigger must not steal Escape from the popover. Accepted cost:
// one Escape over a hovered tooltip inside a drawer/fullscreen surface also
// dismisses that surface — visible and user-initiated, unlike the silent
// "Escape did nothing" a consumed key produces. Attached only while open or a
// show is pending.
watch(
  [isControlled, open, pendingShow],
  ([controlled, isOpen, pending], _, onCleanup) => {
    if (controlled || typeof window === 'undefined') return;
    if (!isOpen && !pending) return;
    window.addEventListener('keydown', onEscape, true);
    onCleanup(() => window.removeEventListener('keydown', onEscape, true));
  },
  { immediate: true, flush: 'sync' }
);

onBeforeUnmount(() => {
  clearPointerShowTimer();
  clearFocusShowTimer();
  clearHideTimer();
  pointerOpen.value = false;
  focusOpen.value = false;
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', onEscape, true);
  }
});
</script>

<template>
  <!-- No wrapper in anchored mode: the caller already has the element the bubble
       should point at, and an extra node there is what it wanted to avoid. -->
  <div
    v-if="$slots.default"
    ref="wrapper"
    class="tooltip-wrapper"
    v-bind="$attrs"
    @mouseenter="armPointerOpen"
    @mouseleave="scheduleHidePointer"
    @focusin="armFocusOpen"
    @focusout="hideFocus"
  >
    <slot></slot>
  </div>

  <!--
    Teleport out of the tree so the bubble is never sized/clipped by a narrow
    rail column (`overflow-x: hidden` on .show-quicknav, ~2rem icon buttons).
    The old in-tree popover API path inherited that constraint and stacked
    labels like "Inbox" letter-by-letter.

    The target is <body>, EXCEPT inside a modal dialog or a popover, where it is
    that element — anything below the top layer would be hidden behind it (see
    `teleportTarget`).

    `v-if`, not `v-show`: `useFloatingPanel` passes `whileElementsMounted:
    autoUpdate`, which starts the moment BOTH elements are in the DOM — a
    ResizeObserver on reference and floating, an IntersectionObserver, and a
    scroll listener on every scrollable ancestor. A merely hidden bubble keeps
    all of that running for the tooltip's whole life, so a grid with one
    tooltip per row recomputed a screenful of invisible bubbles on every scroll
    tick. Rendering it only while open confines the loop to the one bubble that
    is actually on screen. The shared positioning helper keeps the bubble
    transparent until its first position resolves; the `watch` on `open`
    above repositions once the node exists.
  -->
  <Teleport :to="teleportTarget">
    <div
      v-if="open && (props.text || $slots.content)"
      ref="floating"
      popover="manual"
      class="tooltip-popover"
      :class="{
        'is-interactive': props.interactive,
        'is-multiline': props.multiline,
        'is-rich': !!$slots.content,
      }"
      role="tooltip"
      :style="floatingStyles"
      @mouseenter="onBubbleEnter"
      @mouseleave="onBubbleLeave"
    >
      <span class="tooltip-label">
        <slot name="content">{{ props.text }}</slot>
      </span>
      <div ref="arrowEl" class="tooltip-arrow" :style="arrowStyle" />
    </div>
  </Teleport>
</template>

<style scoped>
.tooltip-wrapper {
  display: inline-block;
}
</style>

<style>
/* Unscoped: teleported to body; keep styling global + self-contained. */

.tooltip-popover {
  position: fixed;
  /* A popover's UA style is `inset: 0`; the bubble is placed by left/top. */
  inset: auto;
  z-index: var(--nxt-z-tooltip);
  pointer-events: none;
  box-sizing: border-box;
  /* Size to the label — never shrink to the rail trigger width. */
  width: max-content;
  max-width: min(90vw, var(--nxt-panel-width-xl));
  min-height: 1.75em;
  height: auto;
  margin: 0;
  padding: 0.35em 0.75em;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background-color: var(--nxt-tooltip-bg);
  color: var(--nxt-tooltip-fg);
  box-shadow: var(--nxt-shadow-md);
  overflow: visible;
  /* Isolate from ancestor theme (uppercase rails, tight tracking, 14px forge).
     The bubble must not inherit the anchor's font-size (e.g. NxToggleGroup
     segments), or tooltips render inconsistently. */
  font-family: var(--nxt-font);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  font-style: normal;
  line-height: var(--nxt-line-height-tight);
  letter-spacing: normal;
  text-transform: none;
  /* Wrap at max-width instead of running off-screen. `width: max-content` still
     keeps short labels on one line; long ones break, and an unbreakable token
     (URL, ID) breaks mid-word rather than widening the bubble. */
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
  /* Centering lives in the base rule, not on `:popover-open`: the bubble is
     rendered only while open (`v-if`) and shown as a popover right after, so
     the base rule is what it always looks like. */
  display: flex;
  align-items: center;
  justify-content: center;
}

.tooltip-popover.is-interactive {
  pointer-events: auto;
}

/* Several lines (`multiline`) or rich content (`#content`) read flush left and
   in body weight: a centred, semibold paragraph is hard to follow line by line. */
.tooltip-popover.is-multiline,
.tooltip-popover.is-rich {
  justify-content: flex-start;
  text-align: start;
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-normal);
}
/* `pre-line` keeps the text's line breaks but still wraps long lines at the
   bubble's max-width and collapses runs of spaces. Only for `text`: a slot's
   template whitespace is no line break anyone meant. */
.tooltip-popover.is-multiline:not(.is-rich) .tooltip-label {
  white-space: pre-line;
}

.tooltip-label {
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  overflow: visible;
  text-transform: none;
  letter-spacing: normal;
}

.tooltip-arrow {
  position: absolute;
  width: 0.6em;
  height: 0.6em;
  background-color: inherit;
  transform: rotate(45deg);
  z-index: -1;
  pointer-events: none;
}
</style>
